import React, { useMemo, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { useQuery } from "@apollo/client";
import MaButton from "../../../../Components/UI/ma-button";
import { SettingsSectionCard } from "./settings-section-card";
import { GET_MY_BANKS } from "../../../../Utils/Queries";
import walletBg from "../../../../assets/images/wallet-bg.png";
import { PiBankFill } from "react-icons/pi";
import { AddAccountModal } from "../payouts/add-account-modal";

const maskAccount = (acct) => {
  if (!acct) return "";
  const tail = String(acct).slice(-4);
  return `**** ${tail}`;
};

function PayoutCard({ bankName, accountName, accountNumber, badge }) {
  return (
    <div
      className="rounded-xl bg-gradient-to-r from-[#1A1918] to-[#333333] px-5 py-4 text-white h-[130px] shadow-lg"
      style={{ background: `url(${walletBg})`, backgroundSize: "cover" }}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="my-0 text-[11px] font-medium uppercase tracking-wide text-white/60">
            Linked account
          </p>
          <p className="my-0 mt-2 text-[15px] font-semibold">
            {bankName} · {maskAccount(accountNumber)}
          </p>
        </div>
        {badge ? (
          <span className="rounded-full bg-emerald-500/20 px-2.5 py-0.5 text-[11px] font-bold text-emerald-300">
            {badge}
          </span>
        ) : null}
      </div>
      <div className="mt-10">
        <p className="mt-3 my-0 text-[14px] text-[#FFFFFFb7]">{bankName}</p>
        <p className="my-0 uppercase text-[#FFFFFFb7]">{accountName}</p>
      </div>
    </div>
  );
}

function CtaBtn({ onClick, title, sxs, disabled }) {
  return (
    <MaButton
      variant="primary"
      title={title || "Save Changes"}
      onClick={onClick}
      disabled={disabled}
      sx={{
        bgcolor: "#2E1834",
        minHeight: "44px",
        px: 3,
        borderRadius: "12px",
        fontWeight: 600,
        ...sxs,
      }}
    />
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 py-8 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#EFEAF1] text-[#2E1834]">
        <PiBankFill className="text-[22px]" />
      </div>
      <p className="my-0 text-[14px] font-semibold text-[#151515]">
        No bank account linked yet
      </p>
      <p className="my-0 mt-1 text-[12px] text-[#6B7280]">
        Add a settlement account to receive your payouts.
      </p>
      <div className="mt-4 flex justify-center">
        <CtaBtn onClick={onAdd} title="Add Bank Account" />
      </div>
    </div>
  );
}

export function SettingsWalletPanel() {
  const [addOpen, setAddOpen] = useState(false);

  const { data, loading, refetch } = useQuery(GET_MY_BANKS, {
    fetchPolicy: "cache-and-network",
  });

  const banks = useMemo(() => data?.allMyBanks ?? [], [data?.allMyBanks]);
  const hasBanks = banks.length > 0;

  const openAdd = () => setAddOpen(true);
  const closeAdd = () => setAddOpen(false);

  const handleAdded = () => {
    refetch();
  };

  return (
    <div className="flex flex-col gap-8">
      <Alert
        severity="info"
        className="rounded-[10px] border border-solid border-[#BFDBFE] bg-[#DBEAFE] text-[#1E40AF]"
      >
      <div>
        <p className="my-0 text-[#1A1918] font-black text-[14px]">Payouts are processed weekly</p>
        <p className="my-0 text-[#6B6966] text-[12px]">Funds from completed orders clear after 3–5 business days and are transferred every Friday.</p>
      </div>
      </Alert>

      <SettingsSectionCard
        title="Bank Account Details"
        subtitle="Primary settlement account for withdrawals."
        footer={
          hasBanks ? (
            <div className="flex items-center gap-3">
              <CtaBtn onClick={openAdd} title="Add Bank Account" />
              <CtaBtn
                onClick={() => {}}
                title="Delete Account"
                sxs={{ bgcolor: "#BD3A3A" }}
              />
            </div>
          ) : null
        }
      >
        {loading && !hasBanks ? (
          <div className="flex items-center justify-center py-10">
            <CircularProgress size={28} sx={{ color: "#2E1834" }} />
          </div>
        ) : hasBanks ? (
          <div className="flex flex-col gap-4">
            {banks.map((bank, idx) => (
              <PayoutCard
                key={bank?._id || idx}
                bankName={bank?.bankName}
                accountName={bank?.accountName}
                accountNumber={bank?.accountNumber}
                badge={idx === 0 ? "Active" : undefined}
              />
            ))}
          </div>
        ) : (
          <EmptyState onAdd={openAdd} />
        )}
      </SettingsSectionCard>

      <AddAccountModal
        open={addOpen}
        onClose={closeAdd}
        onSuccess={handleAdded}
      />
    </div>
  );
}
